// Estado compartido de la sincronización automática y manual, aislado por cuenta.
const cycles = new Map();
const runners = new Map();
const failures = new Map();
const listeners = new Set();
const transfers = new Map();
const emit = () => { for (const listener of listeners) listener(); };
export function subscribeVisitaSyncStatus(listener) { listeners.add(listener); return () => listeners.delete(listener); }
export function beginVisitaSyncCycle(userId) {
    const key = Number(userId);
    cycles.set(key, (cycles.get(key) || 0) + 1);
    emit(); let finished = false;
    return () => { if (finished) return; finished = true; cycles.set(key, Math.max(0, (cycles.get(key) || 1) - 1)); emit(); };
}
export function reportVisitaSyncFailure(userId, visitaId, message) {
    let cleanMessage = message;
    if (typeof cleanMessage === 'string') {
        if (/status code 422/i.test(cleanMessage)) {
            cleanMessage = 'Los datos enviados requieren revisión en el servidor.\n\nLos cambios se conservan en el teléfono.';
        } else if (/status code 401/i.test(cleanMessage)) {
            cleanMessage = 'La sesión venció.\n\nIniciá sesión nuevamente para enviar los cambios guardados.';
        } else if (/status code 403/i.test(cleanMessage)) {
            cleanMessage = 'No tenés permiso para enviar estos cambios.\n\nSe conservan en el teléfono.';
        } else if (/status code/i.test(cleanMessage)) {
            cleanMessage = 'El servidor no pudo procesar el envío.\n\nLos cambios se conservan en el teléfono.';
        }
    }
    failures.set(`${Number(userId)}:${Number(visitaId)}`, cleanMessage);
    emit();
}
export function getVisitaSyncActivity(userId, visitaId) {
    const key = `${Number(userId)}:${Number(visitaId)}`;
    const transfer = transfers.get(key);
    return { active: Boolean(transfer?.count), checking: Boolean(cycles.get(Number(userId))), startedAt: transfer?.startedAt, error: failures.get(key) || null };
}
export function beginVisitaSyncTransfer(userId, visitaId) {
    const key = `${Number(userId)}:${Number(visitaId)}`;
    const previous = transfers.get(key);
    transfers.set(key, { count: (previous?.count || 0) + 1, startedAt: previous?.startedAt || Date.now() });
    failures.delete(key);
    emit();
    let finished = false;
    return () => {
        if (finished) return;
        finished = true;
        const current = transfers.get(key);
        if (current?.count > 1) transfers.set(key, { ...current, count: current.count - 1 });
        else transfers.delete(key);
        emit();
    };
}
export async function withVisitaSyncTransfer(userId, visitaId, send) {
    const finish = beginVisitaSyncTransfer(userId, visitaId);
    try { return await send(); } finally { finish(); }
}
export function registerVisitaSyncRunner(userId, runner) {
    const key = Number(userId); runners.set(key, runner);
    return () => { if (runners.get(key) === runner) runners.delete(key); };
}
export async function synchronizeVisitasNow(userId, {reviewedSignatureUuid = null} = {}) {
    const runner = runners.get(Number(userId));
    if (!runner) throw new Error('No se pudo iniciar la sincronización. Volvé a abrir la visita.');
    return runner({ retryFailed: true, ...(reviewedSignatureUuid?{reviewedSignatureUuid}:{}) });
}
export function visitaSyncErrorMessage(error) {
    const status = error?.response?.status;
    if (status === 401) return 'La sesión venció.\n\nIniciá sesión nuevamente para enviar los cambios guardados.';
    if (status === 403) return 'No tenés permiso para enviar estos cambios.\n\nSe conservan en el teléfono.';
    if (status === 422) {
        const backendMessage = Object.values(error?.response?.data?.errors || {}).flat()[0] || error?.response?.data?.message || error?.response?.data?.error;
        if (backendMessage && !/status code/i.test(backendMessage)) {
            return `${backendMessage}\n\nLos cambios se conservan para revisión.`;
        }
        return 'Los datos enviados requieren revisión en el servidor.\n\nLos cambios se conservan en el teléfono.';
    }
    if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') return 'El servidor está demorando.\n\nLos cambios se conservan para reintentar.';
    if (!status) return 'No se pudo conectar con el servidor.\n\nLos cambios se conservan en el teléfono.';
    const backendMessage = Object.values(error?.response?.data?.errors || {}).flat()[0] || error?.response?.data?.message || error?.response?.data?.error;
    if (backendMessage && !/status code/i.test(backendMessage)) {
        return `${backendMessage}\n\nLos cambios se conservan para reintentar.`;
    }
    return 'No se pudo completar el envío.\n\nLos cambios se conservan para reintentar.';
}

// Una lectura nueva sin cambios no debe notificar otra vez a React. La fecha
// solo importa al comprobar una edición que acaba de guardarse localmente.
export function isSameVisitaSyncState(previous, next, savedAt = null) {
    if (previous.identity !== next.identity) return false;
    if (Boolean(previous.snapshot) !== Boolean(next.snapshot)) return false;
    const keys = ['drafts', 'operations', 'photos', 'copies', 'conflicts', 'dependencies', 'total', 'inFlight', 'error'];
    if (keys.some((key) => previous.snapshot?.[key] !== next.snapshot?.[key])) return false;
    if (['active', 'startedAt', 'error'].some((key) => previous.activity?.[key] !== next.activity?.[key])) return false;
    const savedTime = savedAt ? new Date(savedAt).getTime() : 0;
    return !savedTime || (Number(previous.snapshot?.readAt || 0) >= savedTime) === (Number(next.snapshot?.readAt || 0) >= savedTime);
}

export function buildVisitaSyncView({ snapshot, activity = {}, online = true, localSaveStatus, savedAt = null, dirty = false, manual = false }) {
    const pending = snapshot?.total ?? null;
    const active = manual || activity.active || snapshot?.inFlight;
    const error = activity.error || snapshot?.error;
    const pendingText = pending === 1 ? '1 envío pendiente' : `${pending} envíos pendientes`;
    if (localSaveStatus === 'error') return { label: 'No se pudo guardar en el teléfono.\n\nReintentá Guardar.', severity: 'error', active: false };
    if (snapshot?.conflicts > 0) return {label:'Hay un conflicto para revisar · cambios guardados en este dispositivo',severity:'warning',active:false};
    if (snapshot?.dependencies > 0) return {label:'Aplicación parcial · hay una dependencia para revisar; originales conservados',severity:'warning',active:false,blocked:true};
    if (snapshot?.copies > 0 && pending === snapshot.copies) return {label:'Copia actual pendiente de confirmar · originales conservados',severity:'info',active:Boolean(active)};
    if (active && pending > 0 && online) return { label: pending === 1 ? 'Sincronizando… Queda 1 envío' : `Sincronizando… Quedan ${pending} envíos`, severity: 'info', active: true };
    if (localSaveStatus === 'saving') return { label: 'Guardando en el teléfono…', severity: 'info', active: Boolean(active) };
    if (dirty) return { label: 'Cambios sin guardar', severity: 'warning', active: Boolean(active) };
    if (pending == null) return { label: error || 'Comprobando sincronización…', severity: error ? 'warning' : 'info', active: Boolean(active) };
    if (!online && pending > 0) return { label: `Sin conexión · ${pendingText} en el teléfono`, severity: 'warning', active: false };
    if (pending > 0) return { label: error || `Guardado en el teléfono · ${pendingText} de sincronización`, severity: error ? 'warning' : 'info', active: false };
    if (localSaveStatus === 'saved' && savedAt && (!snapshot.readAt || snapshot.readAt < new Date(savedAt).getTime())) return { label: 'Guardado en el teléfono · Comprobando confirmación…', severity: 'info', active: false };
    return { label: online ? 'Sincronizado · No hay envíos pendientes' : 'Sin conexión · Datos guardados en el teléfono', severity: online ? 'success' : 'info', active: false };
}
